import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { CHART_COLORS } from './types';

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function formatCurrencyFull(value: number): string {
  return (
    '$' +
    value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

// ---------------------------------------------------------------------------
// Bar chart data shape
// ---------------------------------------------------------------------------

interface BarDataPoint {
  name: string;
  value: number;
  fill: string;
}

// ---------------------------------------------------------------------------
// Pie chart data shape
// ---------------------------------------------------------------------------

interface PieDataPoint {
  name: string;
  value: number;
}

const PIE_COLORS = [CHART_COLORS.wireFees, CHART_COLORS.fxMargins];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface RevenueChartsProps {
  wireFees: number;
  fxMargins: number;
}

export function RevenueCharts({ wireFees, fxMargins }: RevenueChartsProps) {
  const barData: BarDataPoint[] = [
    { name: 'Wire Fees', value: wireFees, fill: CHART_COLORS.wireFees },
    { name: 'FX Margins', value: fxMargins, fill: CHART_COLORS.fxMargins },
  ];

  const pieData: PieDataPoint[] = [
    { name: 'Wire Fees', value: wireFees },
    { name: 'FX Margins', value: fxMargins },
  ];

  const hasData = wireFees > 0 || fxMargins > 0;

  if (!hasData) {
    return (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div
          className="flex h-[350px] items-center justify-center rounded-[14px] border-[1.5px] border-[#f0f0f0] bg-white shadow-[0_2px_12px_rgba(0,0,0,0.06)]"
          data-testid="bar-chart-empty"
        >
          <p className="text-sm text-[#999]">No revenue data to chart</p>
        </div>
        <div
          className="flex h-[350px] items-center justify-center rounded-[14px] border-[1.5px] border-[#f0f0f0] bg-white shadow-[0_2px_12px_rgba(0,0,0,0.06)]"
          data-testid="pie-chart-empty"
        >
          <p className="text-sm text-[#999]">No revenue data to chart</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* Bar chart — Wire Fees vs FX Margins */}
      <div
        className="rounded-[14px] border-[1.5px] border-[#f0f0f0] bg-white p-6 shadow-[0_2px_12px_rgba(0,0,0,0.06)]"
        data-testid="bar-chart"
      >
        <h2 className="mb-4 text-lg font-bold text-[#1a1a1a]">Revenue by Type</h2>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={barData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={false}
              axisLine={{ stroke: '#f0f0f0' }}
            />
            <YAxis
              tickFormatter={formatCurrency}
              tick={{ fontSize: 12, fill: '#999' }}
              tickLine={false}
              axisLine={{ stroke: '#f0f0f0' }}
              width={65}
            />
            <Tooltip
              formatter={(value: number) => [formatCurrencyFull(value), 'Amount']}
              contentStyle={{
                borderRadius: '8px',
                border: '1px solid #f0f0f0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
            />
            <Legend wrapperStyle={{ fontSize: '13px', color: '#666' }} />
            <Bar
              dataKey="value"
              name="Revenue"
              radius={[6, 6, 0, 0]}
              aria-label="Revenue bar chart"
            >
              {barData.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Pie chart — Revenue composition */}
      <div
        className="rounded-[14px] border-[1.5px] border-[#f0f0f0] bg-white p-6 shadow-[0_2px_12px_rgba(0,0,0,0.06)]"
        data-testid="pie-chart"
      >
        <h2 className="mb-4 text-lg font-bold text-[#1a1a1a]">Revenue Composition</h2>
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              outerRadius={100}
              dataKey="value"
              aria-label="Revenue composition pie chart"
            >
              {pieData.map((entry, index) => (
                <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number) => [formatCurrencyFull(value)]}
              contentStyle={{
                borderRadius: '8px',
                border: '1px solid #f0f0f0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
            />
            <Legend wrapperStyle={{ fontSize: '13px', color: '#666' }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
